import React, { useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Document, Page, pdfjs } from 'react-pdf';
import { v4 as uuidv4 } from 'uuid';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './PDFSignatureViewer.css';
import SignatureDialog from './SignatureDialog';
import SigneesSidebar from './SigneesSidebar';

// Set the worker source
pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';

// Constants from reference implementation
const signatureDimensions = { width: 150, height: 60 };
const PDF_WIDTH_PTS = 600;
const PDF_HEIGHT_PTS = 792;

// Item types for drag and drop
export const ItemTypes = {
  SIGN_BUTTON: 'SIGN_BUTTON',
};

interface SignaturePosition {
  id?: string;
  page: number;
  x: number;
  y: number;
  signed?: boolean;
  signatureUrl?: string;
  signeeName?: string;
  signeeEmail?: string;
  signeeType?: string;
  source?: 'new' | 'loaded';
  signeeId?: string; // Add signeeId to track which signee this field belongs to
  fieldType?: 'signature' | 'initials'; // Add fieldType to distinguish between signature and initials
}

interface PDFSignatureViewerProps {
  pdfFile: string | null;
  currentUserEmail: string;
  onSave?: (pdfBytes: Uint8Array) => void;
  onUpload?: (file: File) => void;
  signees?: Array<{
    name: string;
    email: string;
    signeeType: string;
    color: string;
    level: number;
    fields: Array<{
      type: string;
      label: string;
    }>;
  }>;
  contractDetails?: {
    levels: number;
    yearToRetain: number;
    creatorRole: string;
    contractName: string;
    formData?: any[];
    levelData?: Array<{
      level: number;
      signees: Array<{
        name: string;
        email: string;
        signeeType: string;
        color: string;
        level: number;
        fields: Array<{
          type: string;
          label: string;
        }>;
      }>;
    }>;
  };
  pdfFilePath?: string;
  uuid?: string;
  folderId?: string | null;
}

// DropZone component from reference implementation
const DropZone: React.FC<{
  pageNumber: number;
  onDropSignature: ({
    item,
    page,
    x,
    y,
  }: {
    item: any;
    page: number;
    x: number;
    y: number;
  }) => void;
}> = ({ pageNumber, onDropSignature }) => {
  const dropRef = useRef<HTMLDivElement | null>(null);
  const { useDrop } = require('react-dnd');

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.SIGN_BUTTON,
    drop: (item: any, monitor: any) => {
      const offset = monitor.getClientOffset();
      const sourceOffset = monitor.getSourceClientOffset();
      const dropArea = dropRef.current;

      if (!offset || !dropArea || !sourceOffset) return;

      const rect = dropArea.getBoundingClientRect();
      const top = sourceOffset.y - rect.top;
      const left = sourceOffset.x - rect.left;

      onDropSignature({ item, page: pageNumber, x: left, y: top });
    },
    collect: (monitor: any) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  drop(dropRef);

  return (
    <div
      ref={dropRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        border: isOver ? '2px dashed green' : '2px dashed transparent',
        pointerEvents: 'auto',
      }}
    />
  );
};

// DraggableSignButton component from reference implementation
const DraggableSignButton: React.FC<{
  sx?: React.CSSProperties;
  signatureItem?: SignaturePosition | null;
  enableSign?: boolean;
  onClickMethod?: () => void;
  signeeName?: string;
  signeeType?: string;
  disabled?: boolean;
}> = ({
  sx,
  signatureItem = null,
  enableSign = false,
  onClickMethod,
  signeeName,
  signeeType,
  disabled,
  ...rest
}) => {
  const { useDrag } = require('react-dnd');

  // If the field is an inactive field loaded from a PDF,
  // we render nothing, allowing the underlying PDF content (line and name) to show through.
  if (enableSign && disabled && signatureItem?.source === 'loaded') {
    return null;
  }

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.SIGN_BUTTON,
    item: () => ({ ...signatureItem }),
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <Button
      ref={!enableSign ? (drag as any) : null}
      onClick={(e) => {
        if (enableSign && onClickMethod && !disabled) {
          onClickMethod();
        }
      }}
      disabled={disabled}
      style={{
        border: 'none',
        cursor: disabled ? 'not-allowed' : !enableSign ? 'move' : 'pointer',
        opacity: isDragging || disabled ? 0.5 : 1,
        backgroundColor: 'transparent',
        width: signatureDimensions.width,
        height: signatureDimensions.height,
        zIndex: 10,
        display: isDragging ? 'none' : 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '4px',
        paddingBottom: '8px',
        ...(sx as React.CSSProperties),
      }}
      {...rest}
    >
      <div
        style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}
      >
        {signatureItem?.fieldType === 'initials'
          ? 'Initials Here'
          : 'Sign Here'}
      </div>
      {signeeName && (
        <div
          style={{
            fontSize: '10px',
            color: '#666',
            textAlign: 'center',
            borderTop: '1px solid #ccc',
            paddingTop: '2px',
            width: '100%',
          }}
        >
          {signeeName}
          {signeeType && (
            <div
              style={{
                fontSize: '9px',
                color: '#888',
                marginTop: '1px',
              }}
            >
              ({signeeType})
            </div>
          )}
        </div>
      )}
    </Button>
  );
};

const PDFSignatureViewer: React.FC<PDFSignatureViewerProps> = ({
  pdfFile,
  currentUserEmail,
  onSave,
  onUpload,
  signees = [],
  contractDetails,
  pdfFilePath,
  uuid,
  folderId,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [selectedPostionIndex, setSelectedPostionIndex] = useState<
    number | null
  >(null);
  const [pdfUrlRender, setPdfUrlRender] = useState<string | null>(null);
  const [openSignature, setOpenSignature] = useState(false);
  const [userSignature, setUserSignature] = useState<{
    type: 'draw' | 'type';
    value: string;
  } | null>(null);
  const [pageViewports, setPageViewports] = useState<
    Record<number, { width: number; height: number }>
  >({});
  const [signaturePositions, setSignaturePositions] = useState<
    SignaturePosition[]
  >([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [activeSignerId, setActiveSignerId] = useState<string | null>(null);
  const originalPdfBytesRef = useRef<ArrayBuffer | null>(null);

  // Add signee UUID mapping
  const [signeeUuidMap, setSigneeUuidMap] = useState<Record<string, string>>(
    {}
  );

  // Initialize signee UUID mapping when signees change
  useEffect(() => {
    if (signees.length > 0) {
      const initialUuidMap: Record<string, string> = {};
      signees.forEach((signee) => {
        if (signee.email && !signeeUuidMap[signee.email]) {
          initialUuidMap[signee.email] = uuidv4();
        }
      });

      if (Object.keys(initialUuidMap).length > 0) {
        setSigneeUuidMap((prev) => ({ ...prev, ...initialUuidMap }));
      }
    }
  }, [signees]);

  // Function to get or create signee UUID
  const getSigneeUuid = (signeeEmail: string): string => {
    if (!signeeEmail) {
      return uuidv4(); // Fallback for invalid input
    }

    if (!signeeUuidMap[signeeEmail]) {
      const newUuid = uuidv4();
      setSigneeUuidMap((prev) => ({
        ...prev,
        [signeeEmail]: newUuid,
      }));
      return newUuid;
    }

    return signeeUuidMap[signeeEmail];
  };

  // Function to generate payload with signee UUIDs
  const generatePayloadWithSigneeUuids = () => {
    // Group signature positions by signee
    const signeeGroups: Record<
      string,
      {
        signeeId: string;
        signeeName: string;
        signeeEmail: string;
        signeeType: string;
        fields: SignaturePosition[];
      }
    > = {};

    signaturePositions.forEach((position) => {
      if (position.signeeEmail) {
        const signeeId = getSigneeUuid(position.signeeEmail);

        if (!signeeGroups[position.signeeEmail]) {
          signeeGroups[position.signeeEmail] = {
            signeeId,
            signeeName: position.signeeName || 'Unknown',
            signeeEmail: position.signeeEmail,
            signeeType: position.signeeType || 'Signer',
            fields: [],
          };
        }

        signeeGroups[position.signeeEmail].fields.push(position);
      }
    });

    return {
      signees: Object.values(signeeGroups),
      totalFields: signaturePositions.length,
      contractDetails: contractDetails,
    };
  };

  // Function to validate UUID consistency
  const validateUuidConsistency = () => {
    const emailToUuid: Record<string, string> = {};
    const inconsistencies: string[] = [];

    signaturePositions.forEach((position) => {
      if (position.signeeEmail && position.signeeId) {
        if (!emailToUuid[position.signeeEmail]) {
          emailToUuid[position.signeeEmail] = position.signeeId;
        } else if (emailToUuid[position.signeeEmail] !== position.signeeId) {
          inconsistencies.push(
            `Email ${position.signeeEmail} has different UUIDs: ${emailToUuid[position.signeeEmail]} vs ${position.signeeId}`
          );
        }
      }
    });

    if (inconsistencies.length > 0) {
      return false;
    } else {
      return true;
    }
  };

  // Function to ensure all signature positions have correct signeeId
  const ensureSignaturePositionUuidConsistency = () => {
    let hasChanges = false;
    const updatedPositions = signaturePositions.map((position) => {
      if (
        position.signeeEmail &&
        position.signeeId !== signeeUuidMap[position.signeeEmail]
      ) {
        hasChanges = true;
        return {
          ...position,
          signeeId: signeeUuidMap[position.signeeEmail],
        };
      }
      return position;
    });

    if (hasChanges) {
      setSignaturePositions(updatedPositions);
    }
  };

  // Validation function to check if all signees have at least one signature or initials field
  const validateAllSigneesHaveFields = () => {
    const signeesWithoutFields: string[] = [];

    signees.forEach((signee) => {
      // Check if this signee has any signature or initials fields placed on the PDF
      const hasFields = signaturePositions.some(
        (position) =>
          position.signeeEmail === signee.email &&
          (position.fieldType === 'signature' ||
            position.fieldType === 'initials')
      );

      if (!hasFields) {
        signeesWithoutFields.push(signee.name);
      }
    });

    return {
      isValid: signeesWithoutFields.length === 0,
      missingSignees: signeesWithoutFields,
    };
  };

  // Log mapping when signature positions change
  useEffect(() => {
    if (signaturePositions.length > 0) {
      // Ensure UUID consistency whenever positions or mapping changes
      ensureSignaturePositionUuidConsistency();
    }
  }, [signaturePositions, signeeUuidMap]);

  useEffect(() => {
    // On component mount, check the URL for a signer ID
    const urlParams = new URLSearchParams(window.location.search);
    // const signerId = urlParams.get('signerId');
    // const signerId = '0e29064f-aee6-47db-abb1-e73ad5d09805';
    const signerId = 'a87eae5d-249d-4711-855b-dd52af2910bc';
    if (signerId) {
      setActiveSignerId(signerId);
    }
  }, []);

  // Handle drop signature from reference implementation
  const handleDropSignature = async ({
    item,
    page,
    x,
    y,
  }: {
    item: any;
    page: number;
    x: number;
    y: number;
  }) => {
    // Get or create signee UUID
    const signeeId = getSigneeUuid(item?.signeeEmail);

    const newSign: SignaturePosition = {
      id: uuidv4(), // Keep field ID for individual field tracking
      page,
      x,
      y,
      signed: false,
      signeeName: item?.signeeName,
      signeeEmail: item?.signeeEmail,
      signeeType: item?.signeeType,
      signeeId, // Add signee ID to link field to signee
      source: 'new',
      fieldType: item?.fieldType || 'signature', // Add fieldType from dropped item
    };

    // Add the new signature position to state
    setSignaturePositions((prev) => [...prev, newSign]);

    // Note: We'll embed the visual fields when the PDF is downloaded
    // For now, we keep the interactive components in the UI
    // This ensures the fields remain clickable for signing
  };

  // Load PDF when pdfFile changes
  useEffect(() => {
    const loadPdf = async () => {
      if (!pdfFile) return;
      try {
        const bytes = await fetch(pdfFile).then((res) => res.arrayBuffer());
        originalPdfBytesRef.current = bytes;

        // Try to extract existing signature fields from the PDF
        try {
          const pdfDoc = await PDFDocument.load(bytes);
          const pages = pdfDoc.getPages();
          const existingPositions: SignaturePosition[] = [];

          // Try to extract signature field metadata from PDF document info
          try {
            const subject = pdfDoc.getSubject();

            // Check if this PDF has signature fields by looking at document properties
            if (
              subject?.includes(
                'Document with embedded signature fields - Metadata:'
              )
            ) {
              // Extract metadata from the subject
              const metadataMatch = subject.match(/Metadata: (.+)$/);
              if (metadataMatch) {
                try {
                  const encodedMetadata = metadataMatch[1];
                  const metadataString = atob(encodedMetadata);
                  const metadata = JSON.parse(metadataString);

                  if (
                    metadata.signatureFields &&
                    Array.isArray(metadata.signatureFields)
                  ) {
                    // Restore signature fields from metadata
                    existingPositions.push(
                      ...metadata.signatureFields.map((field: any) => ({
                        id: field.id,
                        page: field.page,
                        x: field.x,
                        y: field.y,
                        signed: field.signed || false,
                        signatureUrl: field.signatureUrl || '',
                        signeeName: field.signeeName || 'Signer',
                        signeeEmail: field.signeeEmail || '',
                        signeeType: field.signeeType || 'Signer',
                        signeeId: field.signeeId, // Restore signee ID
                        source: 'loaded',
                      }))
                    );

                    // Restore signee UUID mapping if available
                    if (metadata.signeeUuidMap) {
                      setSigneeUuidMap(metadata.signeeUuidMap);
                    }
                  }
                } catch (parseError) {
                  console.error(
                    'Error parsing signature field metadata:',
                    parseError
                  );
                }
              }
            }
          } catch (metadataError) {
            // No signature field metadata found in PDF
          }

          setSignaturePositions(existingPositions);

          // Test extraction of signature field data
          if (existingPositions.length > 0) {
            // await extractSignatureFieldData(bytes); // Removed debug call
          }
        } catch (error) {
          setSignaturePositions([]);
        }

        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrlRender(url);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setSnackbar({
          open: true,
          message: 'Error loading PDF',
          severity: 'error',
        });
      }
    };

    loadPdf();
  }, [pdfFile]);

  // On PDF load success (for react-pdf)
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const generatePdfWithFields = async (
    positions: SignaturePosition[]
  ): Promise<Uint8Array> => {
    if (!originalPdfBytesRef.current) {
      throw new Error('Original PDF bytes are not available.');
    }
    const pdfDoc = await PDFDocument.load(originalPdfBytesRef.current);
    const pages = pdfDoc.getPages();
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

    for (const position of positions) {
      const pageIndex = position.page - 1;
      if (pageIndex < 0 || pageIndex >= pages.length) continue;

      const page = pages[pageIndex];
      const { width: pageWidthPoints, height: pageHeightPoints } =
        page.getSize();

      // Get the viewport of the rendered page from state
      const renderedViewport = pageViewports[position.page];
      if (!renderedViewport) {
        console.error(`Viewport for page ${position.page} not found.`);
        continue;
      }

      // Calculate the scale factor between the PDF's native point system
      // and the browser's pixel-based rendering.
      const scale = pageWidthPoints / renderedViewport.width;

      const fieldWidthPx = signatureDimensions.width;
      const fieldHeightPx = signatureDimensions.height;

      // Convert all pixel-based values from the browser to PDF points using the scale factor
      const xInPoints = position.x * scale;
      const yInPoints = position.y * scale;
      const fieldWidthInPoints = fieldWidthPx * scale;
      const fieldHeightInPoints = fieldHeightPx * scale;

      // Flip the Y-axis for pdf-lib's coordinate system (which has its origin at the bottom-left)
      // and add a small upward adjustment to counteract layout inconsistencies.
      const verticalAdjustment = fieldHeightInPoints * 0.25; // Moves field up by 25% of its height
      const yPdf =
        pageHeightPoints - yInPoints - fieldHeightInPoints + verticalAdjustment;

      if (position.signed && position.signatureUrl) {
        try {
          const pngImage = await pdfDoc.embedPng(position.signatureUrl);
          page.drawImage(pngImage, {
            x: xInPoints,
            y: yPdf,
            width: fieldWidthInPoints,
            height: fieldHeightInPoints,
          });
        } catch (error) {
          console.error('Error embedding signature image:', error);
        }
      } else if (position.signeeName) {
        const role = position.signeeType || 'Signer';

        // Calculate text widths for centering
        const nameTextWidth = helveticaFont.widthOfTextAtSize(
          position.signeeName,
          8
        );
        const roleTextWidth = helveticaFont.widthOfTextAtSize(`(${role})`, 6);

        // Center both texts within the field width
        const nameCenterX =
          xInPoints + (fieldWidthInPoints - nameTextWidth) / 2;
        const roleCenterX =
          xInPoints + (fieldWidthInPoints - roleTextWidth) / 2;

        // Position the line and text relative to the signature field, scaling the pixel offsets
        const lineY = yPdf - 5 * scale;
        const nameY = yPdf - 25 * scale; // Name position (more space from line)
        const roleY = yPdf - 35 * scale; // Role position (below name)

        page.drawLine({
          start: {
            x: xInPoints,
            y: lineY,
          },
          end: {
            x: xInPoints + fieldWidthInPoints,
            y: lineY,
          },
          thickness: 1,
        });

        // Draw the name
        page.drawText(position.signeeName, {
          x: nameCenterX,
          y: nameY,
          size: 8,
          font: helveticaFont,
        });

        // Draw the role in parentheses with smaller font
        page.drawText(`(${role})`, {
          x: roleCenterX,
          y: roleY,
          size: 6,
          font: helveticaFont,
        });
      }
    }

    return pdfDoc.save();
  };

  const addSignatureToPDF = async () => {
    if (selectedPostionIndex === null || !userSignature?.value) return;

    try {
      const updatedPositions = [...signaturePositions];
      const activeSignature = signaturePositions[selectedPostionIndex];
      updatedPositions[selectedPostionIndex] = {
        ...activeSignature,
        signed: true,
        signatureUrl: userSignature.value,
      };
      setSignaturePositions(updatedPositions);

      // Regenerate the entire PDF preview with the new signature
      const pdfBytes = await generatePdfWithFields(updatedPositions);
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrlRender(url);

      setSnackbar({
        open: true,
        message: 'Signature added successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error adding signature to PDF:', error);
      setSnackbar({
        open: true,
        message: 'Error adding signature to PDF',
        severity: 'error',
      });
    }
  };

  const onFinalizeSign = (data: { type: 'draw' | 'type'; value: string }) => {
    setUserSignature(data);
  };

  useEffect(() => {
    if (userSignature?.value && selectedPostionIndex !== null) {
      addSignatureToPDF();
    }
  }, [userSignature]);

  const openAddSignaturePopUp = (index: number) => {
    setSelectedPostionIndex(index);
    setOpenSignature(true);
  };

  const handleFinishAndSend = async () => {
    if (!originalPdfBytesRef.current) return;

    // Validate that all signees have at least one signature or initials field
    const validation = validateAllSigneesHaveFields();

    if (!validation.isValid) {
      const message = `\n\n${validation.missingSignees.join('\n')}\n\nPlease add at least one signature or initials field for each signee before proceeding.`;
      setSnackbar({
        open: true,
        message: message,
        severity: 'warning',
      });
      return;
    }

    try {
      // Generate the final PDF with all fields rendered and metadata embedded
      const pdfBytesWithFields =
        await generatePdfWithFields(signaturePositions);
      // Load this temporary PDF to add the final metadata
      const pdfDoc = await PDFDocument.load(pdfBytesWithFields);

      // Store signature field metadata in PDF custom properties
      const metadata = {
        signatureFields: signaturePositions.map((pos) => ({
          id: pos.id,
          page: pos.page,
          x: pos.x,
          y: pos.y,
          signed: pos.signed,
          signatureUrl: pos.signatureUrl,
          signeeName: pos.signeeName,
          signeeEmail: pos.signeeEmail,
          signeeType: pos.signeeType,
          signeeId: pos.signeeId, // Include signee ID in metadata
          fieldType: pos.fieldType, // Include field type in metadata
          source: pos.source,
        })),
        signeeUuidMap, // Include the signee UUID mapping
        version: '1.0',
        timestamp: new Date().toISOString(),
      };

      // Add metadata to PDF document info
      pdfDoc.setTitle('Document with Signature Fields');
      pdfDoc.setAuthor('Secure Docs');
      pdfDoc.setSubject('Document with embedded signature fields');
      pdfDoc.setKeywords(['signature', 'fields', 'secure']);

      // Store metadata in a custom property by encoding it in the subject
      const metadataString = JSON.stringify(metadata);
      pdfDoc.setCreationDate(new Date());
      pdfDoc.setModificationDate(new Date());
      const encodedMetadata = btoa(metadataString);
      pdfDoc.setSubject(
        `Document with embedded signature fields - Metadata: ${encodedMetadata}`
      );

      const finalPdfBytes = await pdfDoc.save();

      // Generate payload with signee UUIDs
      const payload = generatePayloadWithSigneeUuids();

      setSnackbar({
        open: true,
        message:
          'Document sent successfully! PDF created with embedded signature field data.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error in handleFinishAndSend:', error);
      setSnackbar({
        open: true,
        message: 'Error sending document',
        severity: 'error',
      });
    }
  };

  const handlePdfSave = async () => {
    if (!originalPdfBytesRef.current) return;

    // Validate that all signees have at least one signature or initials field
    const validation = validateAllSigneesHaveFields();

    if (!validation.isValid) {
      const message = `\n\n${validation.missingSignees.join('\n')}\n\nPlease add at least one signature or initials field for each signee before proceeding.`;
      setSnackbar({
        open: true,
        message: message,
        severity: 'warning',
      });
      return;
    }

    try {
      // Generate the final PDF with all fields rendered
      const pdfBytesWithFields =
        await generatePdfWithFields(signaturePositions);
      // Load this temporary PDF to add the final metadata
      const pdfDoc = await PDFDocument.load(pdfBytesWithFields);

      // Store signature field metadata in PDF custom properties
      const metadata = {
        signatureFields: signaturePositions.map((pos) => ({
          id: pos.id,
          page: pos.page,
          x: pos.x,
          y: pos.y,
          signed: pos.signed,
          signatureUrl: pos.signatureUrl,
          signeeName: pos.signeeName,
          signeeEmail: pos.signeeEmail,
          signeeType: pos.signeeType,
          signeeId: pos.signeeId, // Include signee ID in metadata
          fieldType: pos.fieldType, // Include field type in metadata
          source: pos.source,
        })),
        signeeUuidMap, // Include the signee UUID mapping
        version: '1.0',
        timestamp: new Date().toISOString(),
      };

      // Add metadata to PDF document info
      pdfDoc.setTitle('Document with Signature Fields');
      pdfDoc.setAuthor('Secure Docs');
      pdfDoc.setSubject('Document with embedded signature fields');
      pdfDoc.setKeywords(['signature', 'fields', 'secure']);

      // Store metadata in a custom property by encoding it in the subject
      const metadataString = JSON.stringify(metadata);
      pdfDoc.setCreationDate(new Date());
      pdfDoc.setModificationDate(new Date());
      const encodedMetadata = btoa(metadataString);
      pdfDoc.setSubject(
        `Document with embedded signature fields - Metadata: ${encodedMetadata}`
      );

      const finalPdfBytes = await pdfDoc.save();
      const blob = new Blob([finalPdfBytes as any], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'signed-document.pdf';
      link.click();

      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message:
          'PDF downloaded successfully! Check console for signature field data.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading PDF',
        severity: 'error',
      });
    }
  };

  const handleEditSignature = () => {
    setOpenSignature(true);
  };

  // Function to generate the contract payload that would be sent with CreateContract action
  const generateContractPayload = () => {
    const payload = generatePayloadWithSigneeUuids();

    // Calculate total number of signees across all levels
    const totalSigneesRequired =
      contractDetails?.formData?.reduce((total, levelData) => {
        return total + levelData.fields.length;
      }, 0) || 0;

    // Create contract data with signee UUIDs (same as in SigneesSidebar)
    const contractData = {
      contractLocation: '', // Will be updated later with actual file path
      signaturesRequired: totalSigneesRequired,
      httpMethod: 'POST',
      actionName: 'CreateContract',
      contractDetails: {
        levels:
          contractDetails?.formData?.map((levelData, index) => ({
            levelId: index + 1,
            signers: levelData.fields.map((signer: any) => {
              // Find the signee UUID for this signer
              const signeeData = payload.signees.find(
                (s) => s.signeeEmail === signer.email
              );
              // Ensure we always use the same UUID for the same email
              const signeeId =
                signeeData?.signeeId ||
                (() => {
                  console.warn(
                    `No signee UUID found for email: ${signer.email}, this should not happen`
                  );
                  return uuidv4(); // Fallback only if something went wrong
                })();
              return {
                email: signer.email,
                signed: false,
                timestamp: null,
                signerFullName: signer.name,
                signeeType: signer.signeeType,
                signeeId: signeeId, // Use consistent signee UUID
              };
            }),
            isCompleted: false,
            isLastLevel: index === (contractDetails?.levels || 0) - 1,
            signedSoFar: 0,
            requiredSignatures: levelData.fields.length,
          })) || [],
        originatorRole: contractDetails?.creatorRole || 'Realtor',
        originatorEmail: currentUserEmail,
        documentTemplate: 'Rental Agreement',
        documentName: contractDetails?.contractName || 'Document',
        originatorUserName: 'Current User', // You might want to get this from auth state
      },
      // Add signee UUIDs data
      signeeData: payload.signees.map((signee) => ({
        signeeId: signee.signeeId,
        signeeName: signee.signeeName,
        signeeEmail: signee.signeeEmail,
        signeeType: signee.signeeType,
        fieldCount: signee.fields.length,
        fields: signee.fields.map((field) => ({
          fieldId: field.id,
          page: field.page,
          x: field.x,
          y: field.y,
          signed: field.signed || false,
          signatureUrl: field.signatureUrl || '',
        })),
      })),
    };

    return contractData;
  };

  // Function to show the contract payload in console and alert

  if (!pdfFile) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Upload a PDF to preview.</Typography>
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Main PDF viewer area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {/* Debug buttons */}

          <Box
            sx={{
              flex: 1,
              position: 'relative',
              background: '#f8f9fa',
              mt: 2,
              mb: 2,
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 80px)', // Increased height
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                p: 2,
                width: '100%',
              }}
            >
              {pdfUrlRender && (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  <Document
                    file={pdfUrlRender}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading="Loading PDF..."
                    error="Failed to load PDF"
                  >
                    {Array.from(new Array(numPages), (el, index) => {
                      const pageNumber = index + 1;
                      return (
                        <Box
                          sx={{ mb: 2, position: 'relative', width: '100%' }}
                          key={`paper_${index + 1}`}
                        >
                          <Page
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            width={Math.max(600, window.innerWidth - 520)}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onRenderSuccess={({ width, height }) => {
                              setPageViewports((prev) => ({
                                ...prev,
                                [index + 1]: { width, height },
                              }));
                            }}
                          />
                          <DropZone
                            pageNumber={pageNumber}
                            onDropSignature={handleDropSignature}
                          />

                          {signaturePositions.map((pos, i) => {
                            if (pos.page === index + 1 && !pos.signed) {
                              const isDisabled =
                                activeSignerId === null ||
                                pos.signeeId !== activeSignerId;

                              return (
                                <DraggableSignButton
                                  enableSign={true}
                                  onClickMethod={() => openAddSignaturePopUp(i)}
                                  signatureItem={pos}
                                  key={`signature-${pageNumber}-${i}`}
                                  sx={{
                                    position: 'absolute',
                                    top: pos.y, // in px already
                                    left: pos.x,
                                    zIndex: 10,
                                  }}
                                  signeeName={pos.signeeName}
                                  signeeType={pos.signeeType}
                                  disabled={isDisabled}
                                />
                              );
                            }
                            // For signed fields, we render nothing here because the
                            // signature is now part of the PDF preview image itself.
                            return null;
                          })}
                        </Box>
                      );
                    })}
                  </Document>
                </Box>
              )}
            </Box>
          </Box>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>

        {/* Sidebar on the right */}
        <SigneesSidebar
          onSidebarFieldDragStart={() => {}}
          onFinishAndSend={handleFinishAndSend}
          onSignClick={() => setOpenSignature(true)}
          userSignature={userSignature}
          onEditSignature={handleEditSignature}
          onDownload={handlePdfSave}
          signees={signees}
          pdfFile={pdfFile}
          fields={[]}
          contractDetails={contractDetails}
          pdfFilePath={pdfFilePath}
          generatePdfWithFields={async () => {
            if (!originalPdfBytesRef.current) {
              throw new Error('Original PDF bytes are not available.');
            }

            // Generate the final PDF with all fields rendered and metadata embedded
            const pdfBytesWithFields =
              await generatePdfWithFields(signaturePositions);
            // Load this temporary PDF to add the final metadata
            const pdfDoc = await PDFDocument.load(pdfBytesWithFields);

            // Store signature field metadata in PDF custom properties
            const metadata = {
              signatureFields: signaturePositions.map((pos) => ({
                id: pos.id,
                page: pos.page,
                x: pos.x,
                y: pos.y,
                signed: pos.signed,
                signatureUrl: pos.signatureUrl,
                signeeName: pos.signeeName,
                signeeEmail: pos.signeeEmail,
                signeeType: pos.signeeType,
                signeeId: pos.signeeId, // Include signee ID in metadata
                fieldType: pos.fieldType, // Include field type in metadata
                source: pos.source,
              })),
              signeeUuidMap, // Include the signee UUID mapping
              version: '1.0',
              timestamp: new Date().toISOString(),
            };

            // Add metadata to PDF document info
            pdfDoc.setTitle('Document with Signature Fields');
            pdfDoc.setAuthor('Secure Docs');
            pdfDoc.setSubject('Document with embedded signature fields');
            pdfDoc.setKeywords(['signature', 'fields', 'secure']);

            // Store metadata in a custom property by encoding it in the subject
            const metadataString = JSON.stringify(metadata);
            pdfDoc.setCreationDate(new Date());
            pdfDoc.setModificationDate(new Date());
            const encodedMetadata = btoa(metadataString);
            pdfDoc.setSubject(
              `Document with embedded signature fields - Metadata: ${encodedMetadata}`
            );

            return await pdfDoc.save();
          }}
          uuid={uuid}
          folderId={folderId}
          signaturePositions={signaturePositions}
          generatePayloadWithSigneeUuids={generatePayloadWithSigneeUuids}
        />

        <SignatureDialog
          open={openSignature}
          onClose={() => setOpenSignature(false)}
          onCreate={onFinalizeSign}
          initialSignature={userSignature}
        />
      </Box>
    </DndProvider>
  );
};

export default PDFSignatureViewer;
